import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import { SORT_DIRECTION } from '@staff-app/shared-types';
import type { OpticlockAgentMonth, SortDirection } from '@staff-app/shared-types';
import { safeDistanceToNow } from '@/lib/safe-format';
import { ProjectSplitBar } from './ProjectSplitBar';

type SortKey = 'agent' | 'latest' | 'total';

interface AgentTimeTableProps {
  rows: OpticlockAgentMonth[];
  loading: boolean;
}

/**
 * The team rollup: one row per agent who logged time this month.
 *
 * Agents with NO entries in the month are absent — that is a property of the server query, not
 * a filter here, so this table cannot accidentally start rendering zero rows.
 *
 * Sorting is client-side over a dozen rows (the server always returns minutes-DESC, and no
 * sort argument exists on the schema). Local useState rather than the persisted zustand store
 * the device table uses — there is no cross-session sort preference worth keeping here.
 */
export function AgentTimeTable({ rows, loading }: AgentTimeTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('total');
  const [dir, setDir] = useState<SortDirection>(SORT_DIRECTION.DESC);

  // The scale every bar is drawn against. Computed across the WHOLE table, which is what makes
  // the bar lengths comparable between rows.
  const maxMinutes = useMemo(() => rows.reduce((m, r) => Math.max(m, r.minutes), 0), [rows]);

  const sorted = useMemo(() => {
    const factor = dir === SORT_DIRECTION.ASC ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sortKey === 'agent') return factor * a.agentEmail.localeCompare(b.agentEmail);
      if (sortKey === 'latest') {
        return factor * String(a.latestCreatedAt ?? '').localeCompare(String(b.latestCreatedAt ?? ''));
      }
      return factor * (a.minutes - b.minutes);
    });
  }, [rows, sortKey, dir]);

  function toggle(key: SortKey): void {
    if (key === sortKey) {
      setDir(dir === SORT_DIRECTION.ASC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC);
    } else {
      setSortKey(key);
      setDir(SORT_DIRECTION.DESC);
    }
  }

  function sortIcon(key: SortKey) {
    if (key !== sortKey) return <ArrowUpDown className="h-3 w-3" />;
    return dir === SORT_DIRECTION.ASC ? (
      <ArrowUp className="h-3 w-3" />
    ) : (
      <ArrowDown className="h-3 w-3" />
    );
  }

  function header(key: SortKey, label: string) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="gap-1 px-1"
        data-testid={`opticlock-sort-${key}`}
        onClick={() => toggle(key)}
      >
        {label}
        {sortIcon(key)}
      </Button>
    );
  }

  return (
    <Table data-testid="opticlock-agent-table">
      <TableHeader>
        <TableRow>
          <TableHead>{header('agent', 'Agent')}</TableHead>
          <TableHead>{header('latest', 'Latest activity')}</TableHead>
          <TableHead>{header('total', 'Total (month)')}</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && rows.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={4}
              className="text-xs text-muted-foreground"
              data-testid="opticlock-agent-loading"
            >
              {'Loading…'}
            </TableCell>
          </TableRow>
        ) : null}
        {!loading && rows.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={4}
              className="text-xs text-muted-foreground"
              data-testid="opticlock-agent-empty"
            >
              {'Nobody logged any time this month.'}
            </TableCell>
          </TableRow>
        ) : null}

        {sorted.map((r) => (
          <TableRow key={r.agentEmail} data-testid={`opticlock-agent-row-${r.agentEmail}`}>
            <TableCell className="font-medium">{r.agentEmail}</TableCell>
            <TableCell data-testid={`opticlock-agent-row-${r.agentEmail}-latest`}>
              <span className="flex flex-col">
                <span className="text-sm">{r.latestProjectName ?? '—'}</span>
                <span className="text-xs text-muted-foreground">
                  {r.latestCreatedAt
                    ? `${safeDistanceToNow(r.latestCreatedAt, undefined, '')} ago`
                    : '—'}
                </span>
              </span>
            </TableCell>
            <TableCell
              className="tabular-nums"
              data-testid={`opticlock-agent-row-${r.agentEmail}-total`}
            >
              {r.hours}
            </TableCell>
            <TableCell>
              <ProjectSplitBar
                email={r.agentEmail}
                minutes={r.minutes}
                maxMinutes={maxMinutes}
                byProject={r.byProject}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
